import { Component, Input } from '@builder.io/sdk';

import { moleculeHeadingFields } from 'molecules/Heading/config';
import imageField, { richImageFields } from 'molecules/Image/config';
import sectionFields from 'molecules/Section/config';
import { videoFields } from 'molecules/Video/config';

const switchbackFields = [
  ...moleculeHeadingFields,
  {
    name: 'reverse',
    type: 'boolean',
    helperText: 'If enabled, places the media (image or video) on the left instead of the right.',
  },
  {
    name: 'aboveTheFold',
    type: 'boolean',
    helperText:
      'Loads this image eagerly with high priority (LCP element). Usually automatic: the first Switchback near the top of a landing page is primed server-side, so you rarely need this. Only set it by hand to force priority onto a different Switchback. Never enable it on more than one per page or they fight for render-critical bandwidth.',
    defaultValue: false,
  },
  {
    name: 'useFeatureCard',
    type: 'boolean',
    helperText: 'Use a feature card with bullet list instead of image/video.',
    defaultValue: false,
  },
  {
    name: 'cardTitle',
    type: 'string',
    helperText: 'Title inside the feature card.',
    showIf: 'options.get("useFeatureCard") === true',
  },
  {
    name: 'cardFeatures',
    type: 'list',
    helperText: 'Feature items for the right-side card.',
    showIf: 'options.get("useFeatureCard") === true',
    subFields: [
      {
        name: 'title',
        type: 'string',
        required: true,
      },
      {
        name: 'description',
        type: 'longText',
        required: true,
      },
      {
        name: 'hasBullet',
        type: 'boolean',
        defaultValue: true,
        helperText: 'Show a checkmark icon for this feature.',
      },
    ],
  },
  {
    name: 'useComparisonBoxes',
    type: 'boolean',
    helperText:
      'Show stacked comparison boxes with live text on the media side (e.g. "Original Embedded iPaaS" vs "Prismatic"). Boxes fade in one at a time.',
    defaultValue: false,
  },
  {
    name: 'comparisonBoxes',
    type: 'list',
    helperText:
      'Stacked outlined boxes. Mark one as highlighted for the teal border, "+" markers, connector node, and watermark.',
    showIf: 'options.get("useComparisonBoxes") === true',
    subFields: [
      {
        name: 'title',
        type: 'string',
        required: true,
        helperText: 'Bold title inside the box.',
      },
      {
        name: 'features',
        type: 'list',
        helperText: 'Feature lines. They flow into two columns on desktop.',
        subFields: [
          {
            name: 'text',
            type: 'string',
            required: true,
          },
        ],
      },
      {
        name: 'isHighlighted',
        type: 'boolean',
        defaultValue: false,
        helperText: 'Teal accent border, "+" markers, connector node on the top edge, and logo watermark.',
      },
    ],
    defaultValue: [
      {
        title: 'Original Embedded iPaaS',
        isHighlighted: false,
        features: [
          { text: 'Productized integrations' },
          { text: 'Low-code integration designer' },
          { text: 'Pre-built connectors' },
          { text: 'White-labeled marketplace' },
          { text: 'Central monitoring and management' },
        ],
      },
      {
        title: 'Prismatic',
        isHighlighted: true,
        features: [
          { text: 'Code-first development' },
          { text: 'Embedded workflows' },
          { text: 'Agentic interactions' },
          { text: 'All on one platform' },
        ],
      },
    ],
  },
  {
    ...imageField('watermarkImage', 'options.get("useComparisonBoxes") === true'),
    helperText: 'Optional logo watermark inside the highlighted box. Defaults to the Prismatic color icon.',
  },
  imageField('backgroundImage'),
  {
    name: 'useLottie',
    type: 'boolean',
    helperText: 'Use Lottie animation instead of image/video.',
    defaultValue: false,
  },
  {
    name: 'lottieFile',
    type: 'file',
    helperText: 'Pick the Lottie JSON file from Cloudinary or upload it.',
  },
  {
    name: 'lottieUrl',
    type: 'string',
    helperText: 'Public URL to the Lottie JSON file (optional override).',
    advanced: true,
  },
  ...richImageFields,
  ...videoFields,
  {
    name: 'disableGradient',
    type: 'boolean',
    helperText: 'Turn off the background gradient effect behind the image/video.',
    defaultValue: false,
  },
  {
    name: 'gradientHeight',
    type: 'number',
    helperText: 'Control the height of the gradient background on desktop (percentage). Default: 80.',
    defaultValue: 80,
    advanced: true,
  },
  {
    name: 'gradientWidth',
    type: 'number',
    helperText: 'Control the width of the gradient background (percentage). Default: 105.',
    defaultValue: 105,
    advanced: true,
  },
  {
    name: 'gradientTop',
    type: 'number',
    helperText: 'Control the top position of the gradient on desktop (pixels). Default: 40.',
    defaultValue: 40,
    advanced: true,
  },
  {
    name: 'mobileGradientHeight',
    type: 'number',
    helperText: 'Control the height of the gradient background on mobile (percentage). Default: 70.',
    defaultValue: 70,
    advanced: true,
  },
  {
    name: 'mobileGradientTop',
    type: 'number',
    helperText: 'Control the top position of the gradient on mobile (pixels). Default: 20.',
    defaultValue: 20,
    advanced: true,
  },
  {
    name: 'mobileGradientBottom',
    type: 'number',
    helperText: 'Control the bottom position of the gradient on mobile (pixels). Default: 60.',
    defaultValue: 60,
    advanced: true,
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const switchbackConfig = {
  name: 'switchback',
  inputs: switchbackFields,
} as Component;

export type SwitchbackConfigProps = GenerateBuilder<typeof switchbackFields> & {
  comparisonBoxes?: {
    title?: string;
    isHighlighted?: boolean;
    features?: { text?: string }[];
  }[];
  watermarkImage?: {
    file?: { url?: string; width?: number; height?: number };
    alt?: string;
  };
};

export default switchbackConfig;
